import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import type {
  CreateActivityRequest,
  QualificationResponse,
  UpdateActivityRequest
} from 'generated';
import { useGetQualifications } from 'queries';
import dayjs from 'dayjs';

import { queries } from 'RestAPIs/rapini';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Box, styled } from '@mui/material';

import { ControlledTextField, showSnackbar } from 'components/shared';
import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { ControlledMultiSelect } from './ControlledMultiSelect';
import activitySchema from '../utils/activitySchema';

import type { ActivityResponse } from 'generated';

const schema = activitySchema();

// Manual type definition due to Yup v0.32 limitations with nested type inference
type Form = {
  name: string;
  code: string;
  description?: string;
  qualifications: QualificationResponse[];
  functions: { id: string; name: string }[];
};

const defaultValues = {
  name: '',
  code: '',
  description: '',
  qualifications: [],
  functions: []
};

interface Props extends FormComponentProps {
  rowData?: ActivityResponse;
}

const SpacedBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacing(4)
}));

export const ActivitiesForm: React.FC<Props> = ({ handleClose, rowData }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: createActivity, isLoading: isCreating } =
    mutations.useCreateActivity();
  const { mutate: updateActivity, isLoading: isUpdating } =
    mutations.useUpdateActivity();
  const { data: allQualifications, isLoading: isLoadingQualifications } =
    useGetQualifications({ enabled: true });

  // The date range is set to 99 years to include all valid functions from now to the future.
  const { data: allFunctions, isLoading: isLoadingFunctions } =
    queries.useGetFunctions(
      dayjs().format('YYYY-MM-DD'),
      dayjs().add(99, 'year').format('YYYY-MM-DD'),
      false,
      undefined,
      false
    );

  const functionOptions =
    allFunctions
      ?.filter((func) => func.entityId != null)
      .map((func) => ({
        id: func.entityId,
        name: func.description || func.code || func.entityId
      })) ?? [];

  const isLoading = isCreating || isUpdating;

  const methods = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { control, handleSubmit, reset } = methods;

  useEffect(() => {
    if (!rowData) {
      reset(defaultValues);
      return;
    }

    // Map allowedFunctionIds back to function options for editing
    const selectedFunctions =
      rowData.allowedFunctionIds
        ?.map((id) => {
          const func = allFunctions?.find((f) => f.entityId === id);
          if (!func || func.entityId == null) return null;
          return {
            id: func.entityId,
            name: func.description || func.code || func.entityId
          };
        })
        .filter((f) => f != null) ?? [];

    reset({
      name: rowData.name,
      code: rowData.code,
      description: rowData.description,
      qualifications: rowData.requiredQualifications,
      functions: selectedFunctions
    });
  }, [rowData, reset, allFunctions]);

  function onSubmit(data: Form) {
    const { name, code, description, qualifications, functions } = data;
    const requiredQualificationEntityIds = qualifications
      .filter((q) => !!q.id)
      .map((q) => q.id);

    const allowedFunctionIds = functions.filter((f) => !!f.id).map((f) => f.id);

    if (rowData) {
      const payload: UpdateActivityRequest = {
        entityId: rowData.entityId,
        name,
        code,
        description,
        requiredQualificationEntityIds,
        schedulingLabel: 'WORKPLACE',
        allowedFunctionIds
      };

      updateActivity(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getActivities.name);
          queryClient.invalidateQueries(['activities', 'list']);
          queryClient.invalidateQueries('planning');
          showSnackbar({ severity: 'success', message: t('changesSaved') });
          handleClose();
        },
        onError: (error: any) => {
          console.error('Failed to update activity:', error);
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
        }
      });

      return;
    }

    const payload: CreateActivityRequest = {
      name,
      code,
      description,
      requiredQualificationEntityIds,
      schedulingLabel: 'WORKPLACE',
      allowedFunctionIds
    };

    createActivity(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.getActivities.name);
        queryClient.invalidateQueries(['activities', 'list']);
        showSnackbar({ severity: 'success', message: t('changesSaved') });
        handleClose();
      },
      onError: (error: any) => {
        console.error('Failed to create activity:', error);
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    });
  }

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <SpacedBox>
        <ControlledTextField
          name="name"
          control={control}
          label={t('name')}
          variant="outlined"
        />
        <ControlledTextField
          name="code"
          control={control}
          label={t('code')}
          variant="outlined"
        />
        <ControlledTextField
          name="description"
          control={control}
          label={t('description')}
          variant="outlined"
        />
        <ControlledMultiSelect
          name="qualifications"
          control={control}
          options={allQualifications ?? []}
          label={t('requiredQualifications')}
          loading={isLoadingQualifications}
        />
        <ControlledMultiSelect
          name="functions"
          control={control}
          options={functionOptions}
          label={t('allowedFunctions')}
          loading={isLoadingFunctions}
        />
      </SpacedBox>
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
          fullWidth
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
